// store.ts
import { checkIfFileExists } from '@hashnode/file-parsing'
import fs from 'fs-extra'
import { generateConfigurationFileAndFolderPaths } from '../utils/hashnode-level-configuration'

export type SessionData = {
	sessionSecret: string
	// These fields are potentially used by Expo CLI.
	userId: string
	username: string
	currentConnection:
		| 'Username-Password-Authentication'
		| 'Browser-Flow-Authentication'
		| 'Personal-Access-Token'
}

export type UserSettingsData = {
	auth?: SessionData | null
}

// The Store class will now enforce the keys and types defined in SessionData
class UserSessionStore {
	private data: Partial<UserSettingsData> = {}
	private filePath

	constructor(cwd: string) {
		const { filePath } = generateConfigurationFileAndFolderPaths(cwd)
		this.filePath = filePath
		this.load()
	}

	private load(): void {
		if (checkIfFileExists(this.filePath)) {
			this.data = fs.readJsonSync(this.filePath)
		}
	}

	private save(): void {
		fs.writeJsonSync(this.filePath, this.data, 'utf8')
	}

	set<K extends keyof UserSettingsData>(key: K, value: UserSettingsData[K]): void {
		this.data[key] = value
		this.save()
	}

	get<K extends keyof UserSettingsData>(key: K): UserSettingsData[K] | undefined {
		return this.data[key]
	}

	// Type-safe method to remove a key-value pair
	remove<K extends keyof UserSettingsData>(key: K): void {
		delete this.data[key]
		this.save()
	}

	// Method to get all the data in the store
	getAll(): Partial<UserSettingsData> {
		return this.data
	}
}

let userSessionStore: UserSessionStore | null = null

export function initUserStore(cwd: string) {
	userSessionStore = new UserSessionStore(cwd)
}

export function getUserSessionStore(): UserSessionStore {
	if (!userSessionStore) {
		throw new Error('User session store not initialized')
	}
	return userSessionStore
}
